import React from 'react';
import { SITE_CONFIG, getCanonicalUrl, truncateText, generateKeywords } from '../lib/seo';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string | string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  schema?: object;
  noindex?: boolean;
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema,
  noindex = false,
}) => {
  const finalTitle = title 
    ? `${title} | ${SITE_CONFIG.name}`
    : SITE_CONFIG.title;
  
  const finalDescription = description 
    ? truncateText(description)
    : SITE_CONFIG.description;
  
  // Usar imagen específica si existe, sino usar la imagen genérica de OG
  const finalImage = image 
    ? (image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`)
    : `${SITE_CONFIG.url}${SITE_CONFIG.defaultOGImage}`;
  
  // La URL canónica siempre debe usar la URL pasada
  // Si no se pasa URL, usar la del home
  const finalUrl = url && url !== '/' 
    ? getCanonicalUrl(url)
    : SITE_CONFIG.url;
  
  const finalKeywords = Array.isArray(keywords)
    ? generateKeywords([], keywords)
    : keywords || 'viajes desde Uruguay, paquetes turísticos, excursiones, viajes en bus, financiación de viajes';

  // React 19 automáticamente mueve estas etiquetas al <head>
  return (
    <>
      {/* Meta tags básicos - Orden optimizado para SEO */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={SITE_CONFIG.name} />
      <meta name="language" content="es" />
      <meta name="geo.region" content="UY" />
      <meta name="geo.placename" content="Montevideo" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL - CRÍTICO para SEO */}
      <link rel="canonical" href={finalUrl} />
      
      {/* Open Graph - Para redes sociales */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />
      <meta property="og:locale:alternate" content="es_ES" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={finalTitle} />
      {SITE_CONFIG.twitterHandle && (
        <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      )}
      
      {/* Meta tags adicionales para SEO e IA */}
      <meta name="theme-color" content="#005F8B" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_CONFIG.name} />
      
      {/* Schema.org JSON-LD - CRÍTICO para SEO e IA */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </>
  );
};

export default SEOHead;

